<script setup lang="ts">
import { legacyOrderKey, legacyOrderCommands, formatting, LegacyRma, LegacyOrder, LegacyAdjustmentCategory } from '@hantera/portal-app';
import { inject, ref } from 'vue';
import RmaAdjustmentForm from './RmaAdjustmentForm.vue';
import Decimal from 'decimal.js';
import useBackend from '@/composition/useBackend';
import { EditableOrderRule } from '@/components/LegacyOrder/editable-order-adjustment';

const props = defineProps<{
  rma: LegacyRma
  adjustment: EditableOrderRule
}>();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));
const backend = useBackend();
const { currency } = formatting;

const showForm = ref(false);
const category = ref<LegacyAdjustmentCategory>();
resetForm();

function openForm() {
  if (props.adjustment.editable) {
    showForm.value = true;
  }
}

function resetForm() {
  props.adjustment.value = props.adjustment.originalValue;

  // Something seems buggy in Hantera backend and the fields are not returned, so
  // we'll match on rule name which is the best we can do right now.
  category.value = order.value.adjustmentCategories.find(r => r.name === props.adjustment.name);
}

function closeForm() {
  resetForm();
  showForm.value = false;
}

async function submit() {
  if (props.adjustment.value.eq(new Decimal(0))) {
    await backend.legacyOrders.buildCommandGroup(
      order.value.orderId,
      'Remove adjustment of {value} from RMA {rmaNumber}',
      {
        value: props.adjustment.originalValue,
        rmaNumber: props.rma.rmaNumber
      }
    )
      .command(legacyOrderCommands.removeRmaAdjustment, {
        rmaId: props.rma.rmaId,
        rmaAdjustmentId: props.adjustment.adjustmentId,
      })
      .command(legacyOrderCommands.addActivityLog, {
        message: 'Removed RMA adjustment {name} with value {value} from RMA {rmaNumber}',
        fields: {
          name: props.adjustment.name,
          value: props.adjustment.originalValue,
          rmaNumber: props.rma.rmaNumber,
        }
      })
      .send();

    closeForm();
    return;
  }

  if (!category.value) {
    // TODO Add error state
    return;
  }

  await backend.legacyOrders.buildCommandGroup(
    order.value.orderId,
    'Set RMA adjustment from {oldValue} to {newValue} of {rmaNumber}',
    {
      oldValue: props.adjustment.originalValue,
      newValue: props.adjustment.value,
      rmaNumber: props.rma.rmaNumber
    }
  )
    .command(legacyOrderCommands.addOrUpdateRmaAdjustment, {
      rmaId: props.rma.rmaId,
      rmaAdjustmentId: props.adjustment.adjustmentId,
      priceWithVat: props.adjustment.value.neg(),
    })
    // Legacy action sets this field but doesn't seem to be any UI for it?
    // .command(upsertRmaAdjustmentField, {
    //   rmaId: props.data.rmaId,
    //   rmaAdjustmentId: adjustmentId,
    //   fieldKey: 'Description',
    //   fieldValue: '',
    // })
    .command(legacyOrderCommands.upsertRmaAdjustmentField, {
      rmaId: props.rma.rmaId,
      rmaAdjustmentId: props.adjustment.adjustmentId,
      fieldKey: 'CategoryCode',
      fieldValue: category.value.code,
    })
    .command(legacyOrderCommands.upsertRmaAdjustmentField, {
      rmaId: props.rma.rmaId,
      rmaAdjustmentId: props.adjustment.adjustmentId,
      fieldKey: 'CategoryDescription',
      fieldValue: category.value.name,
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Set RMA adjustment {name} from {oldValue} to {newValue} of {rmaNumber}',
      fields: {
        oldValue: props.adjustment.originalValue,
        newValue: props.adjustment.value,
        rmaNumber: props.rma.rmaNumber,
        name: category.value.name,
      }
    })
    .send();

  closeForm();
  return;
}
</script>

<template>
  <div
    class="rma-adjustment"
    :class="{
      'rma-adjustment--editable': adjustment.editable
    }"
    @click="openForm"
    v-if="!showForm"
  >
    <div class="rma__adjustment rma__adjustment-label">{{ adjustment.name }}:</div>
    <div class="rma__adjustment rma__adjustment-value">
      {{ currency(adjustment.value, order.currencyCode) }}
    </div>
  </div>
  <div
    class="rma-adjustment__form"
    v-if="showForm"
  >
    <RmaAdjustmentForm
      v-model:selected-category="category"
      v-model:value="adjustment.value"
      @submit="submit"
      @cancel="closeForm"
    />
  </div>
</template>

<style>
.rma-adjustment {
  display: contents;
  color: hsla(var(--color-text), .7);
}

.rma-adjustment--editable {
  cursor: pointer;
}

.rma-adjustment--editable:hover {
  color: hsl(var(--color-text));
}

.rma-adjustment--editable div {
  text-decoration: underline;
  text-decoration-style: dotted;
}

.rma-adjustment__form {
  grid-column: 1 / 3;
}
</style>